<template>
  <Component
    :is="maxSmall ? Drawer : Dialog"
    @update:open="handleUpdateOpen"
    v-bind="maxSmall ? {} : { size: 'full', side: 'default' }"
    :title="$t('components.profile.language.title')"
    showFooter
    :description="$t('components.profile.language.description')"
    :teleport="maxSmall"
  >
    <slot />
    <template #header>
      <Component
        ref="dialog"
        :is="maxSmall ? DrawerHeader : DialogHeader"
        :title="$t('components.profile.language.title')"
        v-bind="
          maxSmall
            ? {
                variant: 'tertiary',
                type: 'gray',
                size: 'xl',
              }
            : {
                description: $t('components.profile.language.description'),
              }
        "
        class="bg-primary md:p-6 md:pb-5 border-b border-secondary"
      />
    </template>
    <template #content>
      <div class="h-full p-6 bg-primary overflow-y-auto">
        <div class="flex flex-col gap-1">
          <div
            v-for="lang of languages"
            :key="lang.value"
            class="flex flex-row border rounded-md px-4 py-2 gap-4 items-center cursor-pointer"
            :class="{
              'border-brand bg-brand-primary_alt': isActive(lang),
              'border-secondary': !isActive(lang),
            }"
            @click="language = lang"
          >
            <Flag :languageCode="lang.value" class="w-5 h-5" />
            <p
              class="font-medium grow"
              :class="{
                'text-secondary': !isActive(lang),
                'text-brand-secondary': isActive(lang),
                'text-style-sm': !maxSmall,
                'text-style-base': maxSmall,
              }"
            >
              {{ lang.label }}
            </p>
            <Icon
              name="checkmark-default"
              v-if="isActive(lang)"
              class="fg-brand-primary"
            />
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div
        class="flex flex-col items-end bg-primary py-4 px-6 md:p-4 gap-2 border-t border-secondary"
        :class="{
          'pt-6': !maxSmall,
          'pt-4': maxSmall,
        }"
      >
        <Button
          :as="DialogClose"
          :label="$t('components.buttons.apply_changes')"
          size="xl"
          class="w-full"
          :disabled="busy || noChange"
          @click.stop="updateLanguage"
        />
        <Button
          v-if="!maxSmall"
          :as="DialogClose"
          :label="$t('components.buttons.cancel')"
          size="xl"
          class="w-full"
          variant="secondary"
          type="gray"
          @click.stop
        />
      </div>
    </template>
  </Component>
</template>

<script setup>
import { computed, ref } from "vue";
import { Dialog, DialogClose, DialogHeader } from "@/SharedComponents/Dialog";
import { Drawer, DrawerHeader } from "@/SharedComponents/Drawer";
import { Button } from "@/SharedComponents/Buttons";
import { Flag } from "@/SharedComponents/Flags";
import { Icon } from "@/SharedComponents/Icon";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { directionStore } from "@/Stores/direction";
import { useI18n } from "vue-i18n";

const { maxSmall } = useBreakpoints();
const { t, messages } = useI18n();

const dialog = ref(null);

const emit = defineEmits(["updateLanguage"]);

const props = defineProps({
  user: { type: Object, required: true },
  errors: { type: Array, default: () => [] },
  busy: { type: Boolean, default: false },
});

const languages = computed(() => {
  return Object.keys(messages.value).map((locale) => {
    return {
      value: locale,
      label: t(`validation.values.locale.${locale}`),
    };
  });
});

const language = ref(
  languages.value.find((lang) => lang.value === props.user.locale)
);

function handleUpdateOpen(open) {
  if (open) {
    language.value = languages.value.find(
      (lang) => lang.value === props.user.locale
    );
  }
}

const isActive = (lang) => {
  return language.value?.value === lang.value;
};

const useDirectionStore = directionStore();

const updateLanguage = () => {
  if (!language.value?.value) return;
  emit("updateLanguage", { locale: language.value.value }, () => {
    useDirectionStore.setDirection(
      language.value.value === "ar" ? "rtl" : "ltr"
    );
  });
};

const noChange = computed(() => {
  return language.value?.value === props.user.locale;
});
</script>
